import { rgba } from 'polished';
import { PropsWithChildren, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import {
	DeleteIcon,
	PlaybackPauseIcon,
	PlaybackPlayIcon,
	PlaybackStopIcon,
} from '@treams/treams-icons';
import { Spinner } from '@treams/treams-ui';

const blink = keyframes`
    0% { opacity: 0.5; }
    50% { opacity: 1.0; }
    100% { opacity: 0.5; }
`;

const RecorderDuration = styled(Box)<{ $recording?: boolean; $blink?: boolean }>`
	color: ${(props) => (props.$recording ? props.theme.colors.danger : props.theme.colors.primary)};
	font-weight: bold;
	font-size: 17px;

	${(props) =>
		props.$blink &&
		css`
			animation: 1s linear ${blink} infinite;
		`}
`;

const RecorderButton = styled(Box)<{ $loading?: boolean; $recording?: boolean }>`
	background: ${(props) =>
		props.$recording ? props.theme.colors.danger : props.theme.colors.primary};
	width: 32px;
	height: 32px;
	padding: 6px;
	border-radius: 16px;

	transition: 0.2s linear background-color;

	svg {
		display: block;
		width: 20px;
		height: 20px;

		path {
			fill: white;
		}
	}

	${(props) =>
		props.$loading
			? `
	background: white;

    padding: 8px;

        `
			: `
	cursor: pointer;

	&:hover {
		background: ${rgba(props.$recording ? props.theme.colors.danger : props.theme.colors.primary, 0.5)};

		&:active {
			background: ${rgba(props.$recording ? props.theme.colors.danger : props.theme.colors.primary, 0.75)};
		}
	}
    `}
`;

const DeleteButton = styled(Box)`
	background: ${(props) => rgba(props.theme.colors.danger, 0.1)};
	width: 32px;
	height: 32px;
	padding: 6px;
	border-radius: 16px;
	cursor: pointer;

	transition: 0.2s linear background-color;

	svg {
		display: block;
		width: 20px;
		height: 20px;

		path {
			fill: ${(props) => props.theme.colors.danger};
		}
	}

	&:hover {
		background: ${(props) => rgba(props.theme.colors.danger, 0.25)};

		&:active {
			background: ${(props) => props.theme.colors.danger};

			svg path {
				fill: white;
			}
		}
	}
`;

function ControlButton(props: PropsWithChildren<{ onClick?: () => Promise<void> }>) {
	const [loading, setLoading] = useState(false);

	return (
		<RecorderButton
			$loading={loading}
			onClick={async () => {
				setLoading(true);
				await props.onClick?.();
				setLoading(false);
			}}
		>
			{loading ? <Spinner /> : props.children}
		</RecorderButton>
	);
}

export default function VoiceMemoControls(props: {
	status: 'recording' | 'playing' | 'paused';
	time: number;
	onPlay?: () => Promise<void>;
	onPause?: () => void;
	onStop?: () => void;
	onCancel?: () => void;
}) {
	return (
		<Flex alignItems="center" justifyContent="center" mt={3}>
			<Flex flexGrow={1} flexShrink={1} flexBasis={0} justifyContent="flex-end">
				<RecorderDuration
					mr={3}
					$blink={props.status === 'recording'}
					$recording={props.status === 'recording'}
					flexGrow={0}
					flexShrink={0}
				>
					{Math.floor(props.time / 1000 / 60).toFixed()}:
					{Math.floor((props.time / 1000) % 60)
						.toFixed()
						.padStart(2, '0')}
				</RecorderDuration>
			</Flex>
			{props.status === 'recording' && (
				<RecorderButton $recording onClick={props.onStop}>
					<PlaybackStopIcon />
				</RecorderButton>
			)}
			{props.status === 'paused' && (
				<ControlButton onClick={props.onPlay}>
					<PlaybackPlayIcon />
				</ControlButton>
			)}
			{props.status === 'playing' && (
				<RecorderButton onClick={props.onPause}>
					<PlaybackPauseIcon />
				</RecorderButton>
			)}
			<Box flexGrow={1} flexShrink={1} flexBasis={0}>
				{props.status === 'paused' && props.onCancel && (
					<Box ml={3}>
						<DeleteButton onClick={props.onCancel}>
							<DeleteIcon />
						</DeleteButton>
					</Box>
				)}
			</Box>
		</Flex>
	);
}
